import { Form, Formik, getIn } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { HtmlContent } from '../../global-component-styles';
import GlobalStyles from '../../global-styles';
import { setAllFieldTouched } from '../../utils/helper';
import { DeleteIcon } from '../../utils/icons';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton,
  TextField, Typography
} from '../../utils/material-ui-core';
import Confirm from '../confirm';

interface ConfirmProps {
  callbackOnSave: (confirmOutput: ConfirmOutput) => void;
  content: string;
  deleteConfirmationText: string;
  label: string;
  noText: string;
  open: boolean;
  showDeleteButton: boolean;
  title: string;
  value: string;
  type: "string" | "boolean" | "number" | "number-pos" | "email";
  required: boolean;
  yesText: string;
  customValidation?: (value: string) => string; // Custom validation function that returns error message or empty string
}

const formDefaultValue = {
  val: -1,
};

const formDefaultTouchValue = {
  val: false,
};

let formValidationSchema = Yup.object().shape({});

const ConfirmInput = ({ title, label, content, value, type, required, yesText, noText, open, showDeleteButton, deleteConfirmationText, callbackOnSave, customValidation }: ConfirmProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;

  const [dialogLoading, setDialogLoading] = useState<boolean>(true);
  const [initialFormValues, setInitialFormValues] = useState<{ val: any }>(formDefaultValue);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState<boolean>(false);

  const openDeleteConfirmDialog = () => {
    setDeleteConfirmDialogOpen(true);
  };

  const deleteConfirm = (confirm: boolean) => {
    setDeleteConfirmDialogOpen(false);
    if (confirm) {
      callbackOnSave({ status: "delete", data: confirm });
    }
  };

  const cancel = () => {
    callbackOnSave({ status: "cancel", data: null });
  };

  const save = () => {
    if (formReference.current.isValid) {
      callbackOnSave({
        status: "save",
        data: formReference.current.values.val,
      });
    }
  };

  useEffect(() => {
    if (open) {
      if (required) {
        if (type === "email") {
          formValidationSchema = Yup.object().shape({
            val: Yup.string()
              .email("Invalid email")
              .required(label + " is required."),
          });
        } else if (type === "number") {
          formValidationSchema = Yup.object().shape({
            val: Yup.number()
              .typeError(label + " must be a number.")
              .required(label + " is required."),
          });
        } else if (type === "number-pos") {
          formValidationSchema = Yup.object().shape({
            val: Yup.number()
              .typeError(label + " must be a number.")
              .required(label + " is required.")
              .positive(label + " must be greater than zero.")
              .min(1, label + " must be greater than zero."),
          });
        } else {
          let stringSchema = Yup.string()
            .trim()
            .required(label + " is required.");
            
          // Add custom validation if provided
          if (customValidation) {
            stringSchema = stringSchema.test('custom-validation', '', function(value) {
              if (!value) return true; // Let required validation handle empty values
              const errorMessage = customValidation(value);
              if (errorMessage) {
                return this.createError({ message: errorMessage });
              }
              return true;
            });
          }
          
          formValidationSchema = Yup.object().shape({
            val: stringSchema,
          });
        }
      }
      setInitialFormValues({ val: value });
      setDialogLoading(false);
    }
  }, [label, open, required, title, type, value, customValidation]);

  useEffect(() => {
    if (!open) {
      setDialogLoading(true);
    }
  }, [open]);

  return (
    <>
      <Confirm title="Confirmation" content={`<p>${deleteConfirmationText}</p>`} yesText="DELETE" noText="CANCEL" open={deleteConfirmDialogOpen} callbackOnClose={() => setDeleteConfirmDialogOpen} callbackOnConfirm={deleteConfirm} />

      {/* <p style="text-align:center"><b>${value}</b></p> */}
      <Dialog className={globalStyles.dialogContainer} open={open} maxWidth={"sm"} fullWidth={true}>
        {dialogLoading ? null : (
          <>
            <DialogTitle id="input-dialog-title" className={globalStyles.dialogTitleContainer}>
              <Typography variant="subtitle1" component="span">
                {title}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Formik
                innerRef={formReference}
                initialValues={initialFormValues}
                validationSchema={formValidationSchema}
                validateOnMount={true}
                initialTouched={formDefaultTouchValue}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values: any) => {
                  save();
                }}
              >
                {({ values, errors, handleChange, setFieldValue, touched, handleBlur, isSubmitting, validateForm }) => (
                  <Form>
                    <DialogContentText className={pageStyles.contentText}>{content !== "" ? <HtmlContent dangerouslySetInnerHTML={{ __html: content }}></HtmlContent> : null}</DialogContentText>

                    <TextField autoFocus required={required} type={type} margin="dense" id="val" name="val" value={values.val} label={label} fullWidth onChange={handleChange} onBlur={handleBlur} error={Boolean(getIn(touched, "val") && getIn(errors, "val"))} helperText={getIn(touched, "val") && getIn(errors, "val")} />
                  </Form>
                )}
              </Formik>

              <Box p={3}></Box>
            </DialogContent>
            <DialogActions>
              {showDeleteButton ? (
                <IconButton onClick={() => openDeleteConfirmDialog()} className={globalStyles.errorText}>
                  <DeleteIcon />
                </IconButton>
              ) : null}

              <Button
                onClick={() => {
                  setAllFieldTouched(formReference, "", formReference.current.values);
                  save();
                }}
                color="secondary"
                variant="contained"
                className={globalStyles.buttonDefault}
              >
                {yesText}
              </Button>
              <Button onClick={() => cancel()} className={globalStyles.buttonDefault}>
                <u>{noText}</u>
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentText: {
      whiteSpace: 'pre-wrap',
      minWidth: "300px",
      paddingBottom: "20px",
      textAlign: "left",
      margin:0
    },
  })
);

export default ConfirmInput;
